import { useEffect, useMemo, useState } from "react";
import type { Klaerpunkte } from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { Icon } from "../../shell/Icon";
import { ArticlePickerDialog } from "./ArticlePickerDialog";

type Klaerpunkt = Klaerpunkte["punkte"][number];

const PRIO_TONE: Record<string, { tone: "bad" | "warn" | ""; label: string; rank: number }> = {
  hoch: { tone: "bad", label: "HOCH", rank: 3 },
  mittel: { tone: "warn", label: "MITTEL", rank: 2 },
  niedrig: { tone: "", label: "NIEDRIG", rank: 1 },
};

/**
 * Extrahiert die positionId aus einem Klärpunkt-ID-Pattern.
 *
 * Match-/Bepreisungs-Phase erzeugen IDs nach Schema:
 *   match-extern-pos-000001
 *   match-no-pre-filter-pos-000002
 *   bepreisung-no-pick-pos-000001
 *   bepreisung-artikel-fehlt-pos-000003
 *
 * Klärpunkte ohne `pos-\d+`-Muster (z.B. PDF-Lesefehler, Tenant-Probleme)
 * landen unter dem virtuellen Gruppen-Key `"__ohne-position__"`.
 */
function extractPositionId(id: string): string | null {
  const m = id.match(/pos-\d+/);
  return m ? m[0] : null;
}

interface KlaerpunktGroup {
  groupKey: string;
  positionId: string | null;
  punkte: Klaerpunkt[];
  maxPrioRank: number;
}

function groupByPosition(punkte: Klaerpunkt[]): KlaerpunktGroup[] {
  const groups = new Map<string, KlaerpunktGroup>();
  for (const kp of punkte) {
    const positionId = extractPositionId(kp.id);
    const groupKey = positionId ?? "__ohne-position__";
    let g = groups.get(groupKey);
    if (!g) {
      g = {
        groupKey,
        positionId,
        punkte: [],
        maxPrioRank: 0,
      };
      groups.set(groupKey, g);
    }
    g.punkte.push(kp);
    const rank = PRIO_TONE[kp.prioritaet]?.rank ?? 0;
    if (rank > g.maxPrioRank) g.maxPrioRank = rank;
  }
  // Sortierung: positionId-Gruppen aufsteigend (pos-000001 zuerst),
  // "Ohne Position" am Schluss.
  const list = Array.from(groups.values());
  list.sort((a, b) => {
    if (!a.positionId && b.positionId) return 1;
    if (a.positionId && !b.positionId) return -1;
    return (a.positionId ?? "").localeCompare(b.positionId ?? "");
  });
  return list;
}

export function KlaerpunkteTab({
  tenantId,
  anfrageId,
  onJumpToMatch,
  onAnyMatchPatch,
}: {
  tenantId: string;
  anfrageId: string;
  onJumpToMatch?: (positionId: string, action: "drawer" | "schnellkalk") => void;
  onAnyMatchPatch?: () => void;
}) {
  const { state, refetch } = useApi(
    () => api.getKlaerpunkte(tenantId, anfrageId),
    [tenantId, anfrageId]
  );
  // Match + Konsol fuer den Direkt-Pick im Drawer: damit der
  // ArticlePickerDialog hier inline geoeffnet werden kann, ohne dass der
  // User in den Match-Tab wechseln muss.
  const matchRes = useApi(
    () => api.getMatch(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );
  const konsolRes = useApi(
    () => api.getKonsolidiert(tenantId, anfrageId).catch(() => null),
    [tenantId, anfrageId]
  );
  const matchData =
    matchRes.state.status === "ok" ? matchRes.state.data : null;
  const konsolData =
    konsolRes.state.status === "ok" ? konsolRes.state.data : null;

  const [local, setLocal] = useState<Klaerpunkte | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [drawerGroupKey, setDrawerGroupKey] = useState<string | null>(null);
  const [pickerForPositionId, setPickerForPositionId] = useState<string | null>(
    null
  );
  const data = local ?? (state.status === "ok" ? state.data : null);

  const pickerContext = useMemo(() => {
    if (!pickerForPositionId || !matchData || !konsolData) return null;
    const matchPos = matchData.positionen.find(
      (p) => p.positionId === pickerForPositionId
    );
    const konsolPos = konsolData.positionen.find(
      (p) => p.positionId === pickerForPositionId
    );
    if (!matchPos || !konsolPos) return null;
    return { matchPos, konsolPos };
  }, [pickerForPositionId, matchData, konsolData]);

  useEffect(() => {
    if (state.status === "ok") setLocal(null);
  }, [state.status, state.data]);

  const groups = useMemo(() => {
    if (!data) return [];
    const visible = showResolved ? data.punkte : data.punkte.filter((p) => !p.resolved);
    return groupByPosition(visible);
  }, [data, showResolved]);

  const drawerGroup = drawerGroupKey
    ? groups.find((g) => g.groupKey === drawerGroupKey) ?? null
    : null;

  if (state.status === "loading" && !local) {
    return (
      <div className="ueber">
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini">// KLÄRPUNKTE LADEN …</span>
          </div>
        </section>
      </div>
    );
  }

  if (state.status === "error" && !local) {
    if (state.error.message.includes("404")) {
      return (
        <div className="ueber">
          <section className="block" style={{ borderColor: "oklch(0.78 0.16 152 / 0.4)" }}>
            <div className="block-head">
              <span className="uppercase-mini" style={{ color: "var(--ok)" }}>
                // KEINE KLÄRPUNKTE
              </span>
            </div>
            <div className="notice-body">
              <div>Die Pipeline hat für diese Anfrage keine offenen Punkte produziert.</div>
            </div>
          </section>
        </div>
      );
    }
    return (
      <div className="ueber">
        <section className="block" style={{ borderColor: "oklch(0.72 0.18 25 / 0.4)" }}>
          <div className="block-head">
            <span className="uppercase-mini" style={{ color: "var(--bad)" }}>
              // KLÄRPUNKTE NICHT LADBAR
            </span>
          </div>
          <div className="notice-body">
            <div className="mono" style={{ color: "var(--bad)" }}>
              {state.error.message}
            </div>
            <div>
              <button type="button" className="btn" onClick={refetch}>
                <Icon name="refresh" size={14} /> Nochmal versuchen
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!data || data.punkte.length === 0) {
    return (
      <div className="ueber">
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini">// KLÄRPUNKTE</span>
          </div>
          <div className="notice-body">
            <div className="dim">Keine Klärpunkte in 97-klaerpunkte.json.</div>
          </div>
        </section>
      </div>
    );
  }

  const offen = data.punkte.filter((p) => !p.resolved).length;
  const geloest = data.punkte.filter((p) => p.resolved).length;
  const totalGroupsOffen = groupByPosition(data.punkte.filter((p) => !p.resolved)).length;

  return (
    <div className="ueber">
      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--warn)" }}>
            // KLÄRPUNKTE · {offen} OFFEN ÜBER {totalGroupsOffen} POSITION{totalGroupsOffen === 1 ? "" : "EN"}
          </span>
          <span className="block-sep" />
          <label
            className="checkbox"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.currentTarget.checked)}
            />
            <span>Gelöste anzeigen ({geloest})</span>
          </label>
        </div>

        <div className="klaer-list">
          {groups.map((group, i) => (
            <KlaerpunktGroupCard
              key={group.groupKey}
              group={group}
              animDelay={i * 30}
              onOpen={() => setDrawerGroupKey(group.groupKey)}
            />
          ))}
        </div>
      </section>

      {drawerGroup && (
        <KlaerpunktDrawer
          group={drawerGroup}
          tenantId={tenantId}
          anfrageId={anfrageId}
          onUpdated={(updated) => setLocal(updated)}
          onClose={() => setDrawerGroupKey(null)}
          onJumpToMatch={onJumpToMatch}
          onAnyMatchPatch={onAnyMatchPatch}
          onRefetch={refetch}
          onMaterialPick={(positionId) => setPickerForPositionId(positionId)}
        />
      )}

      {pickerContext && (
        <ArticlePickerDialog
          tenantId={tenantId}
          positionId={pickerContext.matchPos.positionId}
          positionOz={pickerContext.matchPos.oz}
          originaltext={pickerContext.konsolPos.originaltext}
          lvEinheit={pickerContext.konsolPos.einheit}
          lvMenge={pickerContext.konsolPos.menge}
          currentArtikelCode={pickerContext.matchPos.gewaehlterArtikel}
          onClose={() => setPickerForPositionId(null)}
          onPicked={async (code) => {
            await api.pickMatch(
              tenantId,
              anfrageId,
              pickerContext.matchPos.positionId,
              code
            );
            // Phase-5/6-Sync passiert backend-seitig (siehe phase.ts).
            // Klaerpunkte werden NICHT auto-resolved bei setPick — der User
            // kann via "Alle als geloest" im Drawer Bulk-resolven.
            onAnyMatchPatch?.();
            refetch();
            matchRes.refetch();
          }}
        />
      )}
    </div>
  );
}

/**
 * Eine kompakte Karte pro Position bzw. „Ohne Position"-Gruppe. Zeigt den
 * höchsten Prio-Level + Anzahl + Sample-Thema. Klick öffnet Drawer.
 */
function KlaerpunktGroupCard({
  group,
  animDelay,
  onOpen,
}: {
  group: KlaerpunktGroup;
  animDelay: number;
  onOpen: () => void;
}) {
  const maxPrioName = Object.entries(PRIO_TONE).find(
    ([, p]) => p.rank === group.maxPrioRank
  )?.[0] ?? "niedrig";
  const prio = PRIO_TONE[maxPrioName] ?? PRIO_TONE.niedrig!;
  const allResolved = group.punkte.every((p) => p.resolved);
  const borderLeftColor = allResolved
    ? "var(--ok)"
    : prio.tone === "bad"
      ? "var(--bad)"
      : prio.tone === "warn"
        ? "var(--warn)"
        : "var(--line)";

  const titleLine = group.positionId
    ? `Position ${group.positionId}`
    : "Ohne Position";
  const sample = group.punkte[0];
  const sampleThema = sample?.thema ?? "";

  return (
    <button
      type="button"
      onClick={onOpen}
      className="klaer-row"
      style={{
        animationDelay: `${animDelay}ms`,
        borderLeftColor,
        opacity: allResolved ? 0.7 : 1,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        width: "100%",
      }}
    >
      <div className="klaer-sev">
        <span
          className="klaer-dot"
          style={{
            background: allResolved
              ? "var(--ok)"
              : prio.tone === "bad"
                ? "var(--bad)"
                : "var(--warn)",
            boxShadow: `0 0 8px ${
              allResolved ? "var(--ok)" : prio.tone === "bad" ? "var(--bad)" : "var(--warn)"
            }`,
          }}
        />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`pill ${prio.tone}`} style={{ fontSize: 10 }}>
            {prio.label}
          </span>
          <span className="klaer-t">{titleLine}</span>
          <span
            className="pill"
            style={{ fontSize: 10, background: "var(--bg-elevated)" }}
          >
            {group.punkte.length} Klärpunkt{group.punkte.length === 1 ? "" : "e"}
          </span>
          {allResolved && (
            <span className="pill ok" style={{ fontSize: 10 }}>
              <span className="dot" />
              ALLE GELÖST
            </span>
          )}
        </div>
        <div className="klaer-d">
          {group.punkte.length === 1
            ? sampleThema
            : `${sampleThema} + ${group.punkte.length - 1} weitere`}
        </div>
      </div>
      <div className="mono" style={{ fontSize: 11, color: "var(--fg-faint)" }}>
        bearbeiten →
      </div>
    </button>
  );
}

/**
 * Drawer rechts mit allen Klärpunkten einer Position. Jeder Eintrag kann
 * einzeln gelöst / wieder geöffnet werden.
 */
function KlaerpunktDrawer({
  group,
  tenantId,
  anfrageId,
  onUpdated,
  onClose,
  onJumpToMatch,
  onAnyMatchPatch,
  onRefetch,
  onMaterialPick,
}: {
  group: KlaerpunktGroup;
  tenantId: string;
  anfrageId: string;
  onUpdated: (updated: Klaerpunkte) => void;
  onClose: () => void;
  onJumpToMatch?: (positionId: string, action: "drawer" | "schnellkalk") => void;
  onAnyMatchPatch?: () => void;
  onRefetch?: () => void;
  onMaterialPick?: (positionId: string) => void;
}) {
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkErr, setBulkErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const titleLine = group.positionId
    ? `Position ${group.positionId}`
    : "Klärpunkte ohne Position";
  const openCount = group.punkte.filter((p) => !p.resolved).length;

  async function handleBulkResolve() {
    if (!group.positionId) return;
    setBulkSaving(true);
    setBulkErr(null);
    try {
      const updated = await api.bulkResolveKlaerpunkteByPosition(
        tenantId,
        anfrageId,
        group.positionId,
        { resolved: true }
      );
      onUpdated(updated);
      onClose();
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { hint?: string; error?: string } | null;
        setBulkErr(body?.hint ?? body?.error ?? e.message);
      } else if (e instanceof Error) setBulkErr(e.message);
      else setBulkErr(String(e));
    } finally {
      setBulkSaving(false);
    }
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="Klärpunkte für Position">
        <div className="drawer-head">
          <div style={{ minWidth: 0 }}>
            <div className="uppercase-mini">/ KLÄRPUNKTE / {group.positionId ? "POSITION" : "ALLGEMEIN"}</div>
            <div className="drawer-title">{titleLine}</div>
            <div className="drawer-meta">
              <span className="bv-chip mono">
                {group.punkte.length} Eintr{group.punkte.length === 1 ? "ag" : "äge"}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="drawer-body">
          <div style={{ display: "grid", gap: 12 }}>
            {group.punkte.map((kp) => (
              <KlaerpunktDetail
                key={kp.id}
                kp={kp}
                tenantId={tenantId}
                anfrageId={anfrageId}
                onUpdated={onUpdated}
                onJumpToMatch={onJumpToMatch}
                onAnyMatchPatch={onAnyMatchPatch}
                onRefetch={onRefetch}
                groupPositionId={group.positionId}
                onMaterialPick={onMaterialPick}
              />
            ))}
          </div>
        </div>

        <div className="drawer-foot">
          <span
            className="dim mono"
            style={{
              fontSize: 11,
              color: bulkErr ? "var(--bad)" : undefined,
            }}
          >
            {bulkErr ??
              "Lösung pro Klärpunkt eintragen oder wieder öffnen"}
          </span>
          <div style={{ flex: 1 }} />
          {group.positionId && openCount > 0 && (
            <button
              type="button"
              className="btn primary sm"
              onClick={handleBulkResolve}
              disabled={bulkSaving}
              title={`Alle ${openCount} offenen Klaerpunkte dieser Position auf 'geloest' setzen`}
            >
              {bulkSaving
                ? "Speichere …"
                : `Alle ${openCount} als gelöst`}
            </button>
          )}
          <button type="button" className="btn ghost" onClick={onClose}>
            Schliessen
          </button>
        </div>
      </aside>
    </>
  );
}

function KlaerpunktDetail({
  kp,
  tenantId,
  anfrageId,
  onUpdated,
  onJumpToMatch,
  onAnyMatchPatch,
  onRefetch,
  groupPositionId,
  onMaterialPick,
}: {
  kp: Klaerpunkt;
  tenantId: string;
  anfrageId: string;
  onUpdated: (updated: Klaerpunkte) => void;
  onJumpToMatch?: (positionId: string, action: "drawer" | "schnellkalk") => void;
  onAnyMatchPatch?: () => void;
  onRefetch?: () => void;
  groupPositionId?: string | null;
  onMaterialPick?: (positionId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [resolution, setResolution] = useState(kp.resolution ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<
    null | "match" | "exclude" | "schnellkalk"
  >(null);

  async function send(resolved: boolean) {
    setSaving(true);
    setErr(null);
    try {
      const updated = await api.resolveKlaerpunkt(tenantId, anfrageId, kp.id, {
        resolved,
        resolution: resolution || undefined,
      });
      onUpdated(updated);
      setEditing(false);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { hint?: string; error?: string } | null;
        setErr(body?.hint ?? body?.error ?? e.message);
      } else if (e instanceof Error) setErr(e.message);
      else setErr(String(e));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Quick-Actions im Klaerpunkt-Detail: drei Buttons in Systemfarben
   * korrespondierend zu den drei Match-Aktionen.
   *  - „Match-Detail" → springt in den Match-Tab und oeffnet den Drawer
   *  - „Nicht anbieten" → ruft PATCH direkt auf; das Backend resolved
   *    automatisch alle offenen Klaerpunkte zur Position
   *  - „Schnellkalkulation" → springt in den Match-Tab und oeffnet den
   *    KomponentenDialog
   *  Phase-5 (Preise) + Phase-6 (Angebots-Manifest) werden vom Backend
   *  bei jeder Aktion synchron mit-aktualisiert.
   */
  async function handleSetNichtAngeboten() {
    if (!groupPositionId) return;
    setActionPending("exclude");
    setErr(null);
    try {
      await api.setNichtAngeboten(tenantId, anfrageId, groupPositionId, true);
      onAnyMatchPatch?.();
      onRefetch?.();
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { hint?: string; error?: string } | null;
        setErr(body?.hint ?? body?.error ?? e.message);
      } else if (e instanceof Error) setErr(e.message);
      else setErr(String(e));
    } finally {
      setActionPending(null);
    }
  }

  const prio = PRIO_TONE[kp.prioritaet] ?? { tone: "" as const, label: kp.prioritaet.toUpperCase(), rank: 0 };
  const borderLeftColor = kp.resolved
    ? "var(--ok)"
    : prio.tone === "bad"
      ? "var(--bad)"
      : prio.tone === "warn"
        ? "var(--warn)"
        : "var(--line)";

  return (
    <div
      style={{
        padding: "14px 16px",
        background: "var(--bg-base)",
        border: "1px solid var(--line-soft)",
        borderLeft: `3px solid ${borderLeftColor}`,
        borderRadius: "var(--r)",
        opacity: kp.resolved ? 0.75 : 1,
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span className={`pill ${prio.tone}`} style={{ fontSize: 10 }}>
          {prio.label}
        </span>
        <span style={{ fontWeight: 500, color: "var(--fg)" }}>{kp.thema}</span>
        {kp.resolved && (
          <span className="pill ok" style={{ fontSize: 10 }}>
            <span className="dot" />
            GELÖST
          </span>
        )}
      </div>
      <div className="klaer-d" style={{ marginTop: 4 }}>
        {kp.beschreibung}
      </div>
      {kp.resolution && !editing && (
        <div
          className="warn-line"
          style={{
            marginTop: 8,
            borderLeftColor: "var(--ok)",
            background: "var(--ok-tint)",
            borderColor: "oklch(0.78 0.16 152 / 0.3)",
          }}
        >
          <span style={{ color: "var(--ok)", fontWeight: 600 }}>Lösung:</span>{" "}
          {kp.resolution}
        </div>
      )}
      <div className="mono" style={{ fontSize: 10, color: "var(--fg-faint)", marginTop: 8 }}>
        {kp.id}
      </div>
      {editing && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          <textarea
            className="input"
            value={resolution}
            onChange={(e) => setResolution(e.currentTarget.value)}
            rows={2}
            placeholder="Was wurde geklärt / mit wem / wie gelöst? (optional)"
            style={{ resize: "vertical", fontFamily: "var(--f-sans)" }}
          />
          {err && (
            <div className="mono" style={{ color: "var(--bad)", fontSize: 11 }}>
              {err}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <KlaerpunktQuickActions
              positionId={groupPositionId ?? null}
              actionPending={actionPending}
              setActionPending={setActionPending}
              onMaterialPick={
                onMaterialPick && groupPositionId
                  ? () => onMaterialPick(groupPositionId)
                  : undefined
              }
              onMatchDetail={() => onJumpToMatch?.(groupPositionId!, "drawer")}
              onNichtAnbieten={handleSetNichtAngeboten}
              onSchnellkalk={() =>
                onJumpToMatch?.(groupPositionId!, "schnellkalk")
              }
              disabled={saving || actionPending !== null}
            />
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => {
                setEditing(false);
                setResolution(kp.resolution ?? "");
                setErr(null);
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="btn primary sm"
              disabled={saving}
              onClick={() => send(true)}
            >
              {saving ? "Speichere …" : "Als gelöst markieren"}
            </button>
          </div>
        </div>
      )}
      {!editing && (
        <div
          style={{
            marginTop: 10,
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <KlaerpunktQuickActions
            positionId={groupPositionId ?? null}
            actionPending={actionPending}
            setActionPending={setActionPending}
            onMatchDetail={() => onJumpToMatch?.(groupPositionId!, "drawer")}
            onNichtAnbieten={handleSetNichtAngeboten}
            onSchnellkalk={() =>
              onJumpToMatch?.(groupPositionId!, "schnellkalk")
            }
            disabled={saving || actionPending !== null}
          />
          {err && !editing && (
            <span className="mono" style={{ color: "var(--bad)", fontSize: 11 }}>
              {err}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {!kp.resolved ? (
            <button type="button" className="btn sm" onClick={() => setEditing(true)}>
              bearbeiten
            </button>
          ) : (
            <button
              type="button"
              className="btn ghost sm"
              disabled={saving}
              onClick={() => send(false)}
            >
              {saving ? "…" : "wieder öffnen"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Drei Quick-Action-Buttons in den Systemfarben — werden nur gerendert
 * wenn der Klaerpunkt einer konkreten Position zugeordnet ist.
 *   Match-Detail (cyan/primary)  · Position-Drawer im Match-Tab oeffnen
 *   Nicht anbieten (red/bad)     · Position direkt ausschliessen
 *   Schnellkalk (green/ok)       · Komponenten-Dialog im Match-Tab oeffnen
 */
function KlaerpunktQuickActions({
  positionId,
  actionPending,
  setActionPending,
  onMaterialPick,
  onMatchDetail,
  onNichtAnbieten,
  onSchnellkalk,
  disabled,
}: {
  positionId: string | null;
  actionPending: null | "match" | "exclude" | "schnellkalk";
  setActionPending: (a: null | "match" | "exclude" | "schnellkalk") => void;
  /** Direkt im KP-Drawer den Material-Picker oeffnen, ohne Tab-Wechsel.
   *  `undefined` wenn Match/Konsol noch nicht geladen sind. */
  onMaterialPick?: () => void;
  onMatchDetail: () => void;
  onNichtAnbieten: () => Promise<void>;
  onSchnellkalk: () => void;
  disabled: boolean;
}) {
  if (!positionId) return null;
  return (
    <>
      {onMaterialPick && (
        <button
          type="button"
          className="btn primary sm"
          onClick={() => {
            onMaterialPick();
          }}
          disabled={disabled}
          title="Stammartikel direkt hier auswaehlen (ohne Tab-Wechsel)"
        >
          Material-Pick
        </button>
      )}
      <button
        type="button"
        className="btn sm"
        onClick={() => {
          setActionPending("match");
          onMatchDetail();
          // Tab-Wechsel erfolgt synchron, der Klaerpunkt-Drawer unmountet.
          // Pending-State braucht hier kein Reset.
        }}
        disabled={disabled}
        style={{
          borderColor: "var(--acc)",
          color: "var(--acc)",
        }}
        title="Position im Match-Tab oeffnen (volle Kandidaten-Liste)"
      >
        Match-Detail
      </button>
      <button
        type="button"
        className="btn sm"
        onClick={() => {
          void onNichtAnbieten();
        }}
        disabled={disabled}
        style={{
          borderColor: "var(--bad)",
          color: "var(--bad)",
        }}
        title="Position direkt aus dem Angebot ausschliessen"
      >
        {actionPending === "exclude" ? "…" : "Nicht anbieten"}
      </button>
      <button
        type="button"
        className="btn sm"
        onClick={() => {
          setActionPending("schnellkalk");
          onSchnellkalk();
        }}
        disabled={disabled}
        style={{
          borderColor: "var(--ok)",
          color: "var(--ok)",
        }}
        title="Schnellkalkulation im Match-Tab oeffnen"
      >
        Schnellkalkulation
      </button>
    </>
  );
}
